import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { observer } from 'mobx-react'
import _ from 'lodash'
import {
  Block,
  Button,
  ExpanderContent,
  ExpanderGroup,
  ExpanderTitleButton,
  ExternalLink,
  IconLogin,
  InlineAlert,
  LinkList,
  LinkListItem,
  RouterLink,
  Text,
} from 'suomifi-ui-components'
import { getLoginErrorMessage } from 'common/src/vtj/browser/query-param.util'
import { LanguageCode } from 'common/src/vtj/LanguageCode.enum'
import translateLoginError from 'common-translations/src/vtj/translate-login-error'
import CertificateTypes from 'todistukset-common/src/vtj/CertificateTypes.enum'
import { TodistuksetUser } from 'todistukset-common/src/vtj/types/user'
import { useStore } from 'todistukset-ui/src/vtj/stores/store.util'
import { TodistuksetUi } from 'todistukset-ui/src/vtj/common-types'
import {
  blockReasonTitleKey,
  globalBlockReasons,
  userBlockedReason,
} from 'todistukset-ui/src/vtj/components/blocked-announcement/BlockedAnnouncement.util'
import BlockReason from 'todistukset-ui/src/vtj/components/blocked-announcement/BlockReason.enum'
import {
  CertificateItemList,
  CertificateTypeList,
  Intro,
} from 'todistukset-ui/src/vtj/components/landing-page/LandingPage.sc'
import { getCertPath } from 'todistukset-ui/src/vtj/components/landing-page/path.util'
import LocalizedString, {
  LocalizedParagraphs,
} from 'todistukset-ui/src/vtj/components/localized-string/LocalizedString.react'
import {
  ContentBlock,
  ContentSection,
  Heading,
  StyledExpander,
  TextBlockWithRows,
} from 'todistukset-ui/src/vtj/components/page-content/PageContent.sc'
import PriceList from 'todistukset-ui/src/vtj/components/price-list/PriceList.react'
import BlockedAnnouncement from 'todistukset-ui/src/vtj/components/blocked-announcement/BlockedAnnouncement.react'

interface ExpanderItemConfig {
  todistusType: CertificateTypes
  todistusTypeTranslationKey: string
  mandatoryInfoTranslationKey?: string
  optionalInfoTranslationKey?: string
  id: string
  messageConfig?: {
    showMessage: boolean
    status: 'error' | 'warning' | 'neutral'
    text: string
  }
  extraContentAfterDescription?: React.ReactElement
  extraContentBeforeButton?: React.ReactElement
}

const LandingPage: React.FC = observer(() => {
  const {
    common: { user },
  } = useStore()

  const blockReasons = globalBlockReasons(user)
  return !(blockReasons.length === 0) ? (
    <BlockedAnnouncement blockReason={blockReasons[0]} />
  ) : (
    <LandingPageContent />
  )
})

export default LandingPage

// todo: Known Chrome bug: svg icons may disappear when there is another usage of same file in a child element
const LandingPageContent = observer(() => {
  const { t } = useTranslation()
  const {
    common: { user, lang, queryParameters, flashPaymentCanceledNotification },
  } = useStore()

  const errorMessage = getLoginErrorMessage(queryParameters)

  const getWarningMessage = (
    user: TodistuksetUi.Store.Common['user'],
    todistusType: CertificateTypes
  ): Required<ExpanderItemConfig>['messageConfig'] => {
    const blockReason = userBlockedReason(user, todistusType)
    // If there's no specific warning for a block reason here, we will use `blockReason+Title`.
    const warnings: { [k: string]: string } = {
      [BlockReason.UNDER_18]: t('landingPageMinorUserWarning'),
      [BlockReason.FOREIGN_MUNICIPALITY]: t(
        'landingPageForeignMunicipalityWarning'
      ),
      [BlockReason.EIDAS_AUTHENTICATION]: t(
        'landingPageEidasAuthenticatedWarning'
      ),
      [BlockReason.MARRIED]: t('landingPageMarriedWarning'),
      [BlockReason.IN_REGISTERED_PARTNERSHIP]: t(
        'landingPageInRegisteredPartnershipWarning'
      ),
    }
    if (blockReason) {
      return {
        showMessage: true,
        status: 'error',
        text: warnings[blockReason] || t(blockReasonTitleKey(blockReason)),
      }
    }
    return {
      showMessage: false,
      status: 'neutral',
      text: '',
    }
  }

  const sortedTodistusExpanderItems: ExpanderItemConfig[] = [
    {
      id: 'aet-cert-accordion',
      todistusType: CertificateTypes.AETTODISTUS,
      todistusTypeTranslationKey: 'AetCertificate',
      mandatoryInfoTranslationKey:
        'landingPageAetCertificateMandatoryInfoHeader',
      optionalInfoTranslationKey: 'landingPageAetCertificateOptionalInfoHeader',
      messageConfig: getWarningMessage(user, CertificateTypes.AETTODISTUS),
      extraContentBeforeButton: (
        <Block>
          <LinkList ariaLabelledBy="moreInfo">
            <LinkListItem>
              <ExternalLink
                href={t('landingPageAetLawLinkHref')}
                labelNewWindow={t('opensInNewWindow')}
                underline="initial"
              >
                <LocalizedString stringKey="landingPageAetLawLinkText" />
              </ExternalLink>
            </LinkListItem>
          </LinkList>
          <LocalizedString stringKey="landingPageAetOrderProcessInfoText" />
        </Block>
      ),
    },
    ..._.sortBy(
      [
        {
          id: 'alive-cert-accordion',
          todistusType: CertificateTypes.ELOSSAOLOTODISTUS,
          todistusTypeTranslationKey: 'AliveCertificate',
          messageConfig: getWarningMessage(
            user,
            CertificateTypes.ELOSSAOLOTODISTUS
          ),
        },
        {
          id: 'birth-cert-accordion',
          todistusTypeTranslationKey: 'BirthCertificate',
          todistusType: CertificateTypes.SYNTYMATODISTUS,
          messageConfig: getWarningMessage(
            user,
            CertificateTypes.SYNTYMATODISTUS
          ),
        },
        {
          id: 'marital-status-cert-accordion',
          todistusType: CertificateTypes.SIVIILISAATYTODISTUS,
          todistusTypeTranslationKey: 'MaritalStatusCertificate',
          messageConfig: getWarningMessage(
            user,
            CertificateTypes.SIVIILISAATYTODISTUS
          ),
        },
        {
          id: 'marriage-cert-accordion',
          todistusType: CertificateTypes.AVIOLIITTOTODISTUS,
          todistusTypeTranslationKey: 'MarriageCertificate',
          messageConfig: getWarningMessage(
            user,
            CertificateTypes.AVIOLIITTOTODISTUS
          ),
        },
        {
          id: 'home-location-cert-accordion',
          todistusType: CertificateTypes.ASUINPAIKKATODISTUS,
          todistusTypeTranslationKey: 'HomeLocationCertificate',
          messageConfig: getWarningMessage(
            user,
            CertificateTypes.ASUINPAIKKATODISTUS
          ),
        },
      ],
      (item) => t(`landingPage${item.todistusTypeTranslationKey}Title`)
    ),
    {
      id: 'uaet-cert-accordion',
      todistusType: CertificateTypes.UAETTODISTUS,
      todistusTypeTranslationKey: 'UaetCertificate',
      mandatoryInfoTranslationKey:
        'landingPageUaetCertificateMandatoryInfoHeader',
      optionalInfoTranslationKey: '',
      messageConfig: getWarningMessage(user, CertificateTypes.UAETTODISTUS),
    },
  ]

  const todistusTypesList = sortedTodistusExpanderItems.map((item) =>
    t(`landingPage${item.todistusTypeTranslationKey}Title`)
  )

  return (
    <>
      <InlineAlert
        data-test-id="warning-box-suomifi-disabled"
        status="warning"
        ariaLiveMode="polite"
      >
        <LocalizedString stringKey="suomifiMessagesTemporarilyDisabled" />
      </InlineAlert>

      {flashPaymentCanceledNotification && (
        <InlineAlert data-test-id="info-box-cancel-payment">
          <LocalizedString stringKey="paymentCanceledMessage" />
        </InlineAlert>
      )}
      {errorMessage && (
        <InlineAlert
          status="error"
          data-test-id={`login-error-message-${errorMessage}`}
        >
          {translateLoginError(errorMessage, lang)}
        </InlineAlert>
      )}
      <ContentBlock padding="xl" pr="xxl" data-test-id="landing-page-content">
        <ContentSection>
          <Heading variant="h2">
            <LocalizedString stringKey="orderingTodistukset" />
          </Heading>
          <Intro variant="section">
            <LocalizedParagraphs stringKey="landingPageIntro_paragraphs" />
            <LinkList ariaLabelledBy="orderingTodistukset">
              <LinkListItem>
                <RouterLink
                  asComponent={Link}
                  to={`/${lang}/lisatiedot#todistuksen-tietosisallot`}
                  underline="initial"
                >
                  <LocalizedString stringKey="landingPageIntroLink_text" />
                </RouterLink>
              </LinkListItem>
            </LinkList>
          </Intro>
        </ContentSection>
        <ContentSection>
          <Heading variant="h2" forwardedAs="h3">
            <LocalizedString stringKey="landingPageCertificateListTitle" />
          </Heading>
          <TextBlockWithRows>
            <LocalizedString stringKey="landingPageCertificateListShortDescription" />
            <CertificateTypeList>
              {todistusTypesList.map((name, index) => (
                <li key={index}>{name}</li>
              ))}
            </CertificateTypeList>
          </TextBlockWithRows>
          <ExpanderGroup
            openAllText={t('landingPageOpenAllButton')}
            ariaOpenAllText={t('landingPageOpenAllButtonAria')}
            closeAllText={t('landingPageCloseAllButton')}
            ariaCloseAllText={t('landingPageCloseAllButtonAria')}
          >
            {sortedTodistusExpanderItems.map((props) => (
              <TodistusExpander {...props} key={props.id} />
            ))}
          </ExpanderGroup>
          <LinkList ariaLabelledBy="moreInfo">
            <LinkListItem>
              <ExternalLink
                href={t('landingPageInfoLinkHref_1')}
                labelNewWindow={t('opensInNewWindow')}
                underline="initial"
              >
                <LocalizedString stringKey="landingPageInfoLinkText_1" />
              </ExternalLink>
            </LinkListItem>
            <LinkListItem>
              <RouterLink
                asComponent={Link}
                to={`/${lang}/lisatiedot#todistuksen-tilaaminen`}
                underline="initial"
              >
                <LocalizedString stringKey="landingPageInfoLinkText_2" />
              </RouterLink>
            </LinkListItem>
            <LinkListItem>
              <ExternalLink
                href={t('landingPageInfoLinkHref_2_5')}
                labelNewWindow={t('opensInNewWindow')}
                underline="initial"
              >
                <LocalizedString stringKey="landingPageInfoLinkText_2_5" />
              </ExternalLink>
            </LinkListItem>
            <LinkListItem>
              <RouterLink
                asComponent={Link}
                to={`/${lang}/lisatiedot#todistuksen-tilaaminen`}
                underline="initial"
              >
                <LocalizedString stringKey="landingPageInfoLinkText_3" />
              </RouterLink>
            </LinkListItem>
          </LinkList>
        </ContentSection>
        <ContentSection>
          <Heading variant="h2" forwardedAs="h3">
            <LocalizedString stringKey="landingPageDeliveryHeader" />
          </Heading>
          <Heading variant="h4">
            <LocalizedString stringKey="landingPageDeliveryTitleVtjOte" />
          </Heading>
          <LocalizedParagraphs stringKey="landingPageDeliveryDescriptionVtjOte_paragraphs" />
          <Heading variant="h4">
            <LocalizedString stringKey="landingPageDeliveryTitleAet" />
          </Heading>
          <LocalizedParagraphs stringKey="landingPageDeliveryDescriptionAet_paragraphs" />
        </ContentSection>
        <ContentSection>
          <PriceList />
        </ContentSection>
      </ContentBlock>
    </>
  )
})

const TodistusExpander = ({
  todistusType,
  todistusTypeTranslationKey,
  mandatoryInfoTranslationKey = 'landingPageCertificatesMandatoryInfoHeader',
  optionalInfoTranslationKey = 'landingPageCertificatesOptionalInfoHeader',
  id,
  messageConfig,
  extraContentAfterDescription,
  extraContentBeforeButton,
}: ExpanderItemConfig) => {
  return (
    <StyledExpander
      className="todistusExpander"
      key={id}
      id={id}
      data-test-id={id}
    >
      <ExpanderTitleButton data-test-id={`${id}-button`}>
        <LocalizedString
          stringKey={`landingPage${todistusTypeTranslationKey}Title`}
        />
      </ExpanderTitleButton>
      <ExpanderContent>
        <TextBlockWithRows>
          <LocalizedString
            stringKey={`landingPage${todistusTypeTranslationKey}Description_html`}
          />
        </TextBlockWithRows>

        {extraContentAfterDescription}

        <Block mt="m" mb="m">
          <Text variant="bold">
            <LocalizedString stringKey={mandatoryInfoTranslationKey} />
          </Text>
        </Block>
        <CertificateItemList>
          <LocalizedString
            stringKey={`landingPage${todistusTypeTranslationKey}MandatoryInfoListItems`}
          />
        </CertificateItemList>
        {optionalInfoTranslationKey && (
          <>
            <Block mt="m" mb="m">
              <Text variant="bold">
                <LocalizedString stringKey={optionalInfoTranslationKey} />
              </Text>
            </Block>
            <CertificateItemList>
              <LocalizedString
                stringKey={`landingPage${todistusTypeTranslationKey}OptionalInfoListItems`}
              />
            </CertificateItemList>
          </>
        )}

        {extraContentBeforeButton}

        <ProceedToOrderAction
          enabled={!messageConfig?.showMessage}
          path={getCertPath(todistusType)}
          dataTestId={`${id}-order-button`}
        />

        {messageConfig?.showMessage && (
          <Block mt="s" mb="s">
            <InlineAlert
              data-test-id={`${id}-warning`}
              status={messageConfig.status}
            >
              {messageConfig.text}
            </InlineAlert>
          </Block>
        )}
      </ExpanderContent>
    </StyledExpander>
  )
}

function createHandleExpanderLinkClick(
  path: string,
  user: TodistuksetUser | undefined,
  lang: LanguageCode
) {
  return function () {
    const pathWithLang = `/${lang}${path}`
    const url = user
      ? pathWithLang
      : `/auth/suomifi/login?RelayState=${encodeURIComponent(
          pathWithLang
        )}&lang=${lang}`
    window.location.href = url
  }
}

const ProceedToOrderAction = observer(
  ({
    path = '/',
    dataTestId,
    enabled,
  }: {
    path?: string
    dataTestId: string
    enabled: boolean
  }) => {
    const { t } = useTranslation()
    const {
      common: { user, lang },
    } = useStore()
    const buttonText = user
      ? t('loggedInButtonLabel')
      : t('landingPageLoginAndOrder')

    return (
      <Block mt={'xl'} variant={'div'}>
        <Button
          disabled={!enabled}
          data-test-id={dataTestId}
          icon={user ? undefined : <IconLogin />}
          variant={user ? 'default' : 'secondaryLight'}
          onClick={createHandleExpanderLinkClick(path, user, lang)}
          aria-label={buttonText}
        >
          {buttonText}
        </Button>
      </Block>
    )
  }
)
